import { create } from 'zustand';
import type {
  Goal,
  CreateGoalRequest,
  UpdateGoalRequest,
  GoalStatus,
  Task
} from '../types';
import { goalService } from '../services/goalService';

interface GoalStore {
  // 数据状态
  goals: Goal[];
  selectedGoalId: string | null; // 当前筛选的目标ID

  // UI状态
  loading: {
    goals: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  error: string | null;

  // 目标完成确认状态
  goalCompletionModal: {
    isOpen: boolean;
    goalId: string | null;
    goalTitle: string;
  };

  // 异步操作
  loadGoals: (workspace?: 'personal' | 'team', teamId?: string) => Promise<void>;
  createGoal: (goal: CreateGoalRequest) => Promise<void>;
  updateGoal: (id: string, updates: UpdateGoalRequest) => Promise<void>;
  deleteGoal: (id: string) => Promise<void>;
  getGoal: (id: string) => Promise<Goal | null>;
  refreshGoalProgress: (goalId: string) => Promise<void>;

  // UI操作
  setSelectedGoalId: (goalId: string | null) => void;
  clearError: () => void;
  clearGoals: () => void;

  // 计算属性
  getActiveGoals: () => Goal[];
  getGoalsByStatus: (status: GoalStatus) => Goal[];
  getSubGoals: (parentId: string) => Goal[];
  getRootGoals: () => Goal[];

  // 目标完成检查
  checkGoalCompletion: (goalId: string, tasks: Task[]) => boolean;
  promptGoalCompletion: (goalId: string) => void;
  confirmGoalCompletion: () => Promise<void>;
  closeGoalCompletionModal: () => void;

  // 目标重新激活检查
  checkGoalReactivation: (goalId: string, tasks: Task[]) => boolean;
  reactivateGoal: (goalId: string) => Promise<void>;
}

export const useGoalStore = create<GoalStore>((set, get) => ({
  // 初始状态
  goals: [],
  selectedGoalId: null,
  loading: {
    goals: false,
    create: false,
    update: false,
    delete: false,
  },
  error: null,
  goalCompletionModal: {
    isOpen: false,
    goalId: null,
    goalTitle: '',
  },

  // 异步操作
  loadGoals: async (workspace = 'personal', teamId?: string) => {
    set(state => ({
      loading: { ...state.loading, goals: true },
      error: null
    }));

    try {
      const goals = await goalService.getGoals(workspace, teamId);
      set({ goals, loading: { ...get().loading, goals: false } });
    } catch (error) {
      console.error('加载目标失败:', error);
      set({
        error: error instanceof Error ? error.message : '加载目标失败',
        loading: { ...get().loading, goals: false }
      });
    }
  },

  createGoal: async (goalData: CreateGoalRequest) => {
    set(state => ({
      loading: { ...state.loading, create: true },
      error: null
    }));

    try {
      const newGoal = await goalService.createGoal(goalData);
      set(state => ({
        goals: [...state.goals, newGoal],
        loading: { ...state.loading, create: false }
      }));
    } catch (error) {
      console.error('创建目标失败:', error);
      set({
        error: error instanceof Error ? error.message : '创建目标失败',
        loading: { ...get().loading, create: false }
      });
      throw error;
    }
  },

  updateGoal: async (id: string, updates: UpdateGoalRequest) => {
    set(state => ({
      loading: { ...state.loading, update: true },
      error: null
    }));

    try {
      const updatedGoal = await goalService.updateGoal(id, updates);
      set(state => ({
        goals: state.goals.map(goal =>
          goal.id === id ? updatedGoal : goal
        ),
        loading: { ...state.loading, update: false }
      }));
    } catch (error) {
      console.error('更新目标失败:', error);
      set({
        error: error instanceof Error ? error.message : '更新目标失败',
        loading: { ...get().loading, update: false }
      });
      throw error;
    }
  },

  deleteGoal: async (id: string) => {
    set(state => ({
      loading: { ...state.loading, delete: true },
      error: null
    }));

    try {
      await goalService.deleteGoal(id);
      set(state => ({
        goals: state.goals.filter(goal => goal.id !== id),
        selectedGoalId: state.selectedGoalId === id ? null : state.selectedGoalId,
        loading: { ...state.loading, delete: false }
      }));
    } catch (error) {
      console.error('删除目标失败:', error);
      set({
        error: error instanceof Error ? error.message : '删除目标失败',
        loading: { ...get().loading, delete: false }
      });
      throw error;
    }
  },

  getGoal: async (id: string) => {
    try {
      return await goalService.getGoal(id);
    } catch (error) {
      console.error('获取目标详情失败:', error);
      set({ error: error instanceof Error ? error.message : '获取目标详情失败' });
      return null;
    }
  },

  refreshGoalProgress: async (goalId: string) => {
    try {
      const updatedGoal = await goalService.getGoal(goalId);
      set(state => ({
        goals: state.goals.map(goal =>
          goal.id === goalId ? updatedGoal : goal
        )
      }));
    } catch (error) {
      console.error('刷新目标进度失败:', error);
    }
  },

  // UI操作
  setSelectedGoalId: (goalId: string | null) => {
    set({ selectedGoalId: goalId });
  },

  clearError: () => {
    set({ error: null });
  },

  clearGoals: () => {
    set({
      goals: [],
      selectedGoalId: null,
      loading: {
        goals: false,
        create: false,
        update: false,
        delete: false,
      },
      error: null
    });
  },

  // 计算属性
  getActiveGoals: () => {
    return get().goals.filter(goal => goal.status === 'ACTIVE');
  },

  getGoalsByStatus: (status: GoalStatus) => {
    return get().goals.filter(goal => goal.status === status);
  },

  getSubGoals: (parentId: string) => {
    return get().goals.filter(goal => goal.parentGoalId === parentId);
  },

  getRootGoals: () => {
    return get().goals.filter(goal => !goal.parentGoalId);
  },

  // 目标完成检查
  checkGoalCompletion: (goalId: string, tasks: Task[]) => {
    const goal = get().goals.find(g => g.id === goalId);
    if (!goal || goal.status !== 'ACTIVE') {
      return false;
    }

    // 获取该目标下的所有任务
    const goalTasks = tasks.filter(task => task.goalId === goalId);

    // 如果没有任务，不需要提示
    if (goalTasks.length === 0) {
      return false;
    }

    // 检查是否所有任务都已完成
    const allTasksCompleted = goalTasks.every(task => task.status === 'DONE');

    return allTasksCompleted;
  },

  promptGoalCompletion: (goalId: string) => {
    const goal = get().goals.find(g => g.id === goalId);
    if (!goal) return;

    set({
      goalCompletionModal: {
        isOpen: true,
        goalId: goalId,
        goalTitle: goal.title,
      }
    });
  },

  confirmGoalCompletion: async () => {
    const { goalCompletionModal } = get();
    if (!goalCompletionModal.goalId) return;

    try {
      await get().updateGoal(goalCompletionModal.goalId, { status: 'COMPLETED' });

      // 关闭模态框
      set({
        goalCompletionModal: {
          isOpen: false,
          goalId: null,
          goalTitle: '',
        }
      });

      // updateGoal 已经更新了本地状态，不需要重新加载所有目标
    } catch (error) {
      console.error('更新目标状态失败:', error);
      set({ error: error instanceof Error ? error.message : '更新目标状态失败' });
    }
  },

  closeGoalCompletionModal: () => {
    set({
      goalCompletionModal: {
        isOpen: false,
        goalId: null,
        goalTitle: '',
      }
    });
  },

  // 目标重新激活检查
  checkGoalReactivation: (goalId: string, tasks: Task[]) => {
    const goal = get().goals.find(g => g.id === goalId);
    if (!goal || goal.status !== 'COMPLETED') {
      return false;
    }

    // 获取该目标下的所有任务
    const goalTasks = tasks.filter(task => task.goalId === goalId);

    // 如果没有任务，不需要重新激活
    if (goalTasks.length === 0) {
      return false;
    }

    // 检查是否有任务不是已完成状态
    const hasIncompleteTasks = goalTasks.some(task => task.status !== 'DONE');

    return hasIncompleteTasks;
  },

  reactivateGoal: async (goalId: string) => {
    try {
      await get().updateGoal(goalId, { status: 'ACTIVE' });
    } catch (error) {
      console.error('重新激活目标失败:', error);
      set({ error: error instanceof Error ? error.message : '重新激活目标失败' });
    }
  },
}));